import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import Label from "@/components/Label";
import { useLanguage } from "@/context/LanguageContext";
import { estimation } from "@/content/translations";

export default function EstimationPage() {
    const { lang } = useLanguage();
    const t = estimation[lang];
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        brand: "",
        reference: "",
        year: "",
        condition: "good",
        notes: "",
    });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(false);

        const payload = {
            name: formData.name,
            email: formData.email,
            phone: formData.phone || "—",
            brand: formData.brand,
            reference: formData.reference || "—",
            year: formData.year || "—",
            condition: formData.condition,
            notes: formData.notes || "—",
            _subject: `Valuation request — ${formData.name}`,
        };

        try {
            // Formspree endpoint. Replace FORM_ID with the real one from formspree.io.
            const endpoint = "https://formspree.io/f/FORM_ID";
            const res = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify(payload),
            });

            if (res.ok) {
                setSubmitted(true);
                setFormData({ name: "", email: "", phone: "", brand: "", reference: "", year: "", condition: "good", notes: "" });
            } else {
                throw new Error("Formspree error");
            }
        } catch (err) {
            // Fallback: open the user's mail client with the details prefilled.
            const body = Object.entries(payload)
                .filter(([k]) => k !== "_subject")
                .map(([k, v]) => `${k}: ${v}`)
                .join("%0A");
            window.location.href = `mailto:contact@timemetria.com?subject=${encodeURIComponent(payload._subject)}&body=${body}`;
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Header />
            <main data-testid="estimation-page" className="tm-page-in">
                <section className="max-w-[1360px] mx-auto px-8 sm:px-16 pt-24 sm:pt-32">
                    <Reveal>
                        <Label>{t.label}</Label>
                        <h1 className="tm-serif text-[3.25rem] sm:text-[5rem] leading-[0.95] mt-6 text-[hsl(var(--tm-anthracite))]">
                            {t.title}
                        </h1>
                        <div className="mt-8 h-px w-28 bg-[hsl(var(--tm-gold))] tm-line-draw"></div>
                        <p className="mt-10 tm-serif text-[1.35rem] leading-relaxed text-[hsl(var(--tm-anthracite-soft))] max-w-2xl">
                            {t.intro}
                        </p>
                    </Reveal>
                </section>

                <section className="max-w-[1360px] mx-auto px-8 sm:px-16 mt-28 sm:mt-36">
                    <div className="tm-divider mb-16"></div>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
                        <Reveal className="lg:col-span-8 lg:col-start-3">
                            {submitted ? (
                                <div className="tm-surface p-10 sm:p-14 text-center tm-fade-up">
                                    <p className="tm-serif text-2xl text-[hsl(var(--tm-anthracite))]">{t.formSuccess}</p>
                                    <button
                                        onClick={() => setSubmitted(false)}
                                        className="mt-8 tm-label tm-gold-link"
                                    >
                                        {lang === "pt" ? "Fazer outro pedido" : lang === "fr" ? "Faire une autre demande" : "Submit another request"}
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-8">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        <div>
                                            <label className="tm-label block mb-3 text-[hsl(var(--tm-anthracite))]">{t.formName} *</label>
                                            <input
                                                type="text"
                                                name="name"
                                                required
                                                value={formData.name}
                                                onChange={handleChange}
                                                className="w-full px-5 py-4 bg-[hsl(var(--tm-ivory-deep))] border border-[hsl(var(--tm-hairline))] text-[hsl(var(--tm-anthracite))] tm-serif text-lg focus:outline-none focus:border-[hsl(var(--tm-gold))] transition-colors"
                                            />
                                        </div>
                                        <div>
                                            <label className="tm-label block mb-3 text-[hsl(var(--tm-anthracite))]">{t.formEmail} *</label>
                                            <input
                                                type="email"
                                                name="email"
                                                required
                                                value={formData.email}
                                                onChange={handleChange}
                                                className="w-full px-5 py-4 bg-[hsl(var(--tm-ivory-deep))] border border-[hsl(var(--tm-hairline))] text-[hsl(var(--tm-anthracite))] tm-serif text-lg focus:outline-none focus:border-[hsl(var(--tm-gold))] transition-colors"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        <div>
                                            <label className="tm-label block mb-3 text-[hsl(var(--tm-anthracite))]">{t.formPhone}</label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                className="w-full px-5 py-4 bg-[hsl(var(--tm-ivory-deep))] border border-[hsl(var(--tm-hairline))] text-[hsl(var(--tm-anthracite))] tm-serif text-lg focus:outline-none focus:border-[hsl(var(--tm-gold))] transition-colors"
                                            />
                                        </div>
                                        <div>
                                            <label className="tm-label block mb-3 text-[hsl(var(--tm-anthracite))]">{t.formYear}</label>
                                            <input
                                                type="text"
                                                name="year"
                                                placeholder="1970, 1985, etc"
                                                value={formData.year}
                                                onChange={handleChange}
                                                className="w-full px-5 py-4 bg-[hsl(var(--tm-ivory-deep))] border border-[hsl(var(--tm-hairline))] text-[hsl(var(--tm-anthracite))] tm-serif text-lg focus:outline-none focus:border-[hsl(var(--tm-gold))] transition-colors"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        <div>
                                            <label className="tm-label block mb-3 text-[hsl(var(--tm-anthracite))]">{t.formBrand} *</label>
                                            <input
                                                type="text"
                                                name="brand"
                                                required
                                                placeholder="Rolex, Patek Philippe, etc"
                                                value={formData.brand}
                                                onChange={handleChange}
                                                className="w-full px-5 py-4 bg-[hsl(var(--tm-ivory-deep))] border border-[hsl(var(--tm-hairline))] text-[hsl(var(--tm-anthracite))] tm-serif text-lg focus:outline-none focus:border-[hsl(var(--tm-gold))] transition-colors"
                                            />
                                        </div>
                                        <div>
                                            <label className="tm-label block mb-3 text-[hsl(var(--tm-anthracite))]">{t.formReference}</label>
                                            <input
                                                type="text"
                                                name="reference"
                                                placeholder="3135, 6542, etc"
                                                value={formData.reference}
                                                onChange={handleChange}
                                                className="w-full px-5 py-4 bg-[hsl(var(--tm-ivory-deep))] border border-[hsl(var(--tm-hairline))] text-[hsl(var(--tm-anthracite))] tm-serif text-lg focus:outline-none focus:border-[hsl(var(--tm-gold))] transition-colors"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="tm-label block mb-3 text-[hsl(var(--tm-anthracite))]">{t.formCondition}</label>
                                        <select
                                            name="condition"
                                            value={formData.condition}
                                            onChange={handleChange}
                                            className="w-full px-5 py-4 bg-[hsl(var(--tm-ivory-deep))] border border-[hsl(var(--tm-hairline))] text-[hsl(var(--tm-anthracite))] tm-serif text-lg focus:outline-none focus:border-[hsl(var(--tm-gold))] transition-colors"
                                        >
                                            <option value="excellent">{t.conditionExcellent}</option>
                                            <option value="good">{t.conditionGood}</option>
                                            <option value="fair">{t.conditionFair}</option>
                                            <option value="poor">{t.conditionPoor}</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="tm-label block mb-3 text-[hsl(var(--tm-anthracite))]">{t.formNotes}</label>
                                        <textarea
                                            name="notes"
                                            rows={6}
                                            value={formData.notes}
                                            onChange={handleChange}
                                            placeholder={lang === "pt" ? "Ex: caixa original, papéis completos, revisão em 2022..." : lang === "fr" ? "Ex: boîte d'origine, papiers complets, révision en 2022..." : "E.g.: original box, complete papers, serviced in 2022..."}
                                            className="w-full px-5 py-4 bg-[hsl(var(--tm-ivory-deep))] border border-[hsl(var(--tm-hairline))] text-[hsl(var(--tm-anthracite))] tm-serif text-lg focus:outline-none focus:border-[hsl(var(--tm-gold))] transition-colors resize-none"
                                        />
                                    </div>

                                    <div className="pt-4">
                                        <p className="tm-label text-xs text-[hsl(var(--tm-muted))] mb-6">{t.privacyNote}</p>
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="tm-btn-primary disabled:opacity-50"
                                        >
                                            {loading ? t.formSending : t.formSubmit}
                                        </button>
                                    </div>

                                    {error && (
                                        <div className="p-6 bg-red-50 border border-red-200 text-red-700 tm-serif">
                                            {t.formError}
                                        </div>
                                    )}
                                </form>
                            )}
                        </Reveal>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
